import React, { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { TabGroup, TabItem } from "@/components/ui/TabGroup";
import { EditorClientSelector } from "./components/EditorClientSelector";
import { useEditorClient } from "./contexts/EditorClientContext";
import { usePortalAuth } from "@/core/auth/hooks/usePortalAuth";
import { RoleProvider } from "@/contexts/RoleContext";
import { UserRole } from "@/services/urlService";

export const EditorView: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentClientInfo } = useEditorClient();
  const portalAuth = usePortalAuth({ clientId: clientId! });
  const { isAuthenticated, isCheckingSession, logout } = portalAuth;
  const token = portalAuth.token ?? '';
  const clientName = portalAuth.clientName ?? '';

  // Debug logging for token
  useEffect(() => {
    console.log('🔍 EditorView - Auth state:', {
      clientId,
      isAuthenticated,
      isCheckingSession,
      hasToken: !!token,
      tokenLength: token?.length || 0,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });
  }, [clientId, isAuthenticated, isCheckingSession, token]);

  useEffect(() => {
    if (!isCheckingSession && !isAuthenticated) {
      navigate(`/editor/${clientId}/login`);
    }
  }, [isCheckingSession, isAuthenticated, clientId, navigate]);

  // Set current client info in context when clientId or clientName changes
  useEffect(() => {
    if (clientId && clientName) {
      setCurrentClientInfo(clientId, clientName);
    }
  }, [clientId, clientName, setCurrentClientInfo]);

  const handleLogout = async () => {
    await logout();
    navigate(`/editor/${clientId}/login`);
  };

  // Determine active tab from current route
  const getActiveTab = (): string => {
    if (location.pathname.includes("/profile")) return "profile";
    if (location.pathname.includes("/library")) return "library";
    return "library"; // default
  };

  const handleTabChange = (tabId: string) => {
    if (tabId === "library") {
      navigate(`/editor/${clientId}/library/images`);
    } else {
      navigate(`/editor/${clientId}/${tabId}`);
    }
  };

  const navigationTabs: TabItem[] = [
    {
      id: "library",
      label: "Library",
      icon: (
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    // {
    //   id: 'profile',
    //   label: 'Profile',
    //   icon: (
    //     <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    //     </svg>
    //   ),
    // },
  ];

  if (isCheckingSession) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  const navigationConfig = {
    variant: "navigation" as const,
    size: "sm" as const,
    layout: "horizontal" as const,
    hasBackground: true,
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Fixed Header */}
      <header className="bg-white border-b border-gray-200 shrink-0">
        <div className="px-4 lg:px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left side - Navigation */}
            <TabGroup
              items={navigationTabs}
              activeId={getActiveTab()}
              onTabChange={handleTabChange}
              config={navigationConfig}
            />

            {/* Right side - Client selector and logout */}
            <div className="flex items-center space-x-3">
              <EditorClientSelector />
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                  />
                </svg>
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 overflow-y-auto">
        <RoleProvider role={UserRole.EDITOR} clientId={clientId} token={token}>
          <Outlet context={{ clientId, clientName, editorToken: token }} />
        </RoleProvider>
      </main>
    </div>
  );
};
